import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Mon volet roulant est bloqué en position basse, que faire ?', a: 'Ne forcez surtout pas ! Tenter de remonter un volet bloqué en forçant peut aggraver la panne (lames tordues, moteur grillé). Contactez-nous : nous nous déplaçons rapidement à Lyon pour diagnostiquer et débloquer votre volet en toute sécurité.' },
  { q: 'Quelles sont les causes d\'un volet roulant bloqué ?', a: 'Les causes les plus fréquentes sont : une lame sortie du rail, un axe de enroulement désaxé, un tablier qui s\'est décalé dans le caisson, un moteur en butée, ou un corps étranger coincé dans les coulisses. Notre diagnostic gratuit permet d\'identifier précisément l\'origine du blocage.' },
  { q: 'Intervenez-vous en urgence pour un volet bloqué à Lyon ?', a: 'Oui, nous traitons les blocages de volet roulant en priorité, car cela peut représenter un risque de sécurité (accès impossible ou au contraire ouverture non sécurisée). Nous intervenons sous 24 à 48 heures, 7j/7.' },
];

export default function VoletBloque() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Volet roulant bloqué à Lyon ? Déblocage rapide 7j/7 | DFPRO"
        description="Volet roulant bloqué en position haute ou basse à Lyon ? Déblocage rapide par technicien qualifié, 7j/7. Diagnostic gratuit, garantie 10 ans."
        canonicalUrl="https://depanfenetres-lyon.fr/volet-roulant-bloque-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-bloque-2026.png" alt="Volet roulant bloqué débloqué par un technicien DFPRO Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Volet bloqué</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Volet roulant bloqué à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Votre volet roulant est coincé, impossible de le remonter ou de le descendre ? Nos techniciens interviennent rapidement pour le débloquer en toute sécurité.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Pourquoi votre volet roulant est-il bloqué ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Un volet roulant qui se bloque est un problème fréquent que nous rencontrons régulièrement lors de nos interventions à Lyon. Plusieurs causes peuvent expliquer ce blocage, et il est important de les identifier correctement pour appliquer la bonne solution.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    La cause la plus courante est une lame sortie du rail de guidage. Avec le temps et les variations de température, les lames peuvent se dilater ou se rétracter, ce qui peut provoquer leur sortie du rail. Une fois une lame sortie, l'ensemble du tablier se bloque et le volet ne peut plus ni monter ni descendre.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Une autre cause fréquente est un axe d'enroulement désaxé. Si le tablier ne s'enroule plus correctement autour de l'axe, il peut se coincer dans le caisson. Ce problème survient souvent après une manipulation brutale ou un choc sur le volet. Le tablier peut également se décaler latéralement, ce qui empêche les lames de coulisser correctement dans les rails.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Dans le cas d'un volet roulant électrique, le blocage peut être d'origine mécanique (lame sortie, axe désaxé) ou électrique (moteur en butée, condensateur défectueux, fin de course déréglé). Notre technicien réalise un diagnostic complet pour déterminer l'origine exacte de la panne avant toute intervention.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Ne forcez jamais un volet bloqué</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    La première réaction face à un volet bloqué est souvent d'essayer de le forcer. C'est une erreur qui peut transformer une réparation simple en intervention lourde. Forcer sur un volet bloqué peut entraîner des dégâts importants : lames tordues ou cassées, moteur grillé, axe tordu, ou même caisson endommagé.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Si votre volet roulant est bloqué, la meilleure chose à faire est de nous appeler. Nous intervenons à Lyon et dans toute la métropole pour diagnostiquer et résoudre le problème rapidement, sans risque d'aggraver la panne.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Notre méthode de déblocage</h2>
                  <div className="mt-4 space-y-4">
                    {[
                      { title: '1. Diagnostic visuel', desc: 'Nous inspectons le volet, les rails, le caisson et le mécanisme pour identifier l\'origine du blocage.' },
                      { title: '2. Remise en position', desc: 'Lame rentrée dans le rail, axe réaligné, tablier recentré : nous remettons le mécanisme en position sans forcer.' },
                      { title: '3. Test de fonctionnement', desc: 'Une fois le volet débloqué, nous effectuons plusieurs cycles complets pour nous assurer que tout fonctionne parfaitement.' },
                      { title: '4. Conseils d\'entretien', desc: 'Nous vous donnons des conseils simples pour éviter que le blocage ne se reproduise (lubrification, nettoyage des rails).' },
                    ].map((step, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-background-50">{idx + 1}</span>
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-sm text-foreground-800">{step.title}</h3>
                          <p className="mt-1 text-sm text-secondary-500 leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Autres pannes volet roulant</h3>
                  <nav className="space-y-1">
                    <Link to="/volet-roulant-ne-remonte-plus-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Volet qui ne remonte plus</Link>
                    <Link to="/reparation-moteur-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation moteur</Link>
                    <Link to="/changer-lame-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Remplacement de lames</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — volet roulant bloqué</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Volet roulant bloqué à Lyon ?" subtitle="Ne forcez pas, appelez-nous. Déblocage rapide 7j/7, devis gratuit." />
      </main>
      <Footer />
    </>
  );
}